import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { api } from '../lib/api'

const BASE = 'https://paktdoc.com/api/v1'

interface WebhookEndpoint {
  id: string; url: string; events: string[]
  secret: string; active: boolean; createdAt: string
}

const ALL_EVENTS = [
  { value: 'document.completed', desc: 'Todos os signatários assinaram' },
  { value: 'signer.signed',      desc: 'Um signatário individualmente assinou' },
  { value: 'document.cancelled', desc: 'Documento cancelado pelo remetente' },
  { value: 'signer.rejected',    desc: 'Signatário recusou (identidade não confirmada)' },
]

type Lang = 'curl' | 'node' | 'python'

function LangTabs({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="flex gap-1 mb-0 px-4 pt-3">
      {(['curl', 'node', 'python'] as Lang[]).map(l => (
        <button key={l} onClick={() => setLang(l)}
          className={`text-xs px-3 py-1 rounded-t font-mono transition-colors ${lang === l ? 'bg-gray-800 text-green-400' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
          {l === 'node' ? 'Node.js' : l}
        </button>
      ))}
    </div>
  )
}

function Code({ children }: { children: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="relative bg-gray-900 rounded-b rounded-tr overflow-hidden">
      <button onClick={() => { navigator.clipboard.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
        className="absolute top-2 right-3 text-xs text-gray-500 hover:text-gray-300 font-mono transition-colors">
        {copied ? '✓ copiado' : 'copiar'}
      </button>
      <pre className="text-green-400 text-xs p-4 overflow-x-auto leading-relaxed whitespace-pre">{children.trim()}</pre>
    </div>
  )
}

function Section({ id, title, sub, children }: { id?: string; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
      <div className="px-6 py-5 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {sub && <p className="text-sm text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  )
}

function Pill({ color, children }: { color: string; children: React.ReactNode }) {
  return <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded ${color}`}>{children}</span>
}

function Row({ method, path, desc, auth }: { method: string; path: string; desc: string; auth: string }) {
  const mc: Record<string, string> = { GET: 'bg-blue-100 text-blue-700', POST: 'bg-green-100 text-green-700', DELETE: 'bg-red-100 text-red-700', PUT: 'bg-yellow-100 text-yellow-700' }
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-2.5 pr-3"><Pill color={mc[method] ?? 'bg-gray-100 text-gray-600'}>{method}</Pill></td>
      <td className="py-2.5 pr-4 font-mono text-xs text-gray-800">{BASE.replace('https://paktdoc.com', '')}{path}</td>
      <td className="py-2.5 pr-4 text-sm text-gray-600">{desc}</td>
      <td className="py-2.5 text-xs text-gray-400">{auth}</td>
    </tr>
  )
}

export default function ApiSettings() {
  const navigate = useNavigate()
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [keyVisible, setKeyVisible] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([])
  const [loadingWebhooks, setLoadingWebhooks] = useState(true)
  const [copied, setCopied] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [newEvents, setNewEvents] = useState<string[]>(['document.completed'])
  const [addingWebhook, setAddingWebhook] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, boolean>>({})
  const [lang, setLang] = useState<Lang>('curl')

  useEffect(() => {
    const token = localStorage.getItem('pakt_token')
    if (!token) { navigate('/login'); return }
    async function load() {
      try {
        const res = await api.get<{ user: { apiKey: string } }>('/api/auth/me')
        setApiKey(res.user.apiKey)
      } catch { navigate('/login') }
      try {
        const endpoints = await api.get<WebhookEndpoint[]>('/api/auth/webhooks')
        setWebhooks(endpoints)
      } catch { /* ignore */ }
      setLoadingWebhooks(false)
    }
    load()
  }, [navigate])

  async function regenerate() {
    if (!confirm('Ao regenerar, a chave atual para de funcionar imediatamente. Continuar?')) return
    setRegenerating(true)
    try {
      const res = await api.post<{ apiKey: string }>('/api/auth/api-key/regenerate')
      setApiKey(res.apiKey); setKeyVisible(true)
    } catch { alert('Erro ao regenerar chave') }
    setRegenerating(false)
  }

  function copy() {
    if (apiKey) { navigator.clipboard.writeText(apiKey); setCopied(true); setTimeout(() => setCopied(false), 2000) }
  }

  async function addWebhook() {
    if (!newUrl || newEvents.length === 0) return
    setAddingWebhook(true)
    try {
      const endpoint = await api.post<WebhookEndpoint>('/api/auth/webhooks', { url: newUrl, events: newEvents })
      setWebhooks(prev => [endpoint, ...prev])
      setNewUrl(''); setNewEvents(['document.completed']); setShowForm(false)
      setRevealedSecrets(prev => ({ ...prev, [endpoint.id]: true }))
    } catch { alert('Erro ao criar webhook') }
    setAddingWebhook(false)
  }

  async function deleteWebhook(id: string) {
    if (!confirm('Remover este endpoint?')) return
    await api.delete(`/api/auth/webhooks/${id}`)
    setWebhooks(prev => prev.filter(w => w.id !== id))
  }

  const maskedKey = apiKey ? `${apiKey.slice(0, 8)}${'•'.repeat(48)}${apiKey.slice(-4)}` : ''

  const KEY = apiKey ?? 'SUA_API_KEY'

  const snippets: Record<string, Record<Lang, string>> = {
    auth: {
      curl: `curl ${BASE}/me \\\n  -H "Authorization: Bearer ${KEY}"`,
      node: `const res = await fetch('${BASE}/me', {\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})\nconst { user } = await res.json()`,
      python: `import requests\nr = requests.get('${BASE}/me',\n  headers={'Authorization': f'Bearer {API_KEY}'})\nprint(r.json())`,
    },
    create: {
      curl: `curl -X POST ${BASE}/documents \\\n  -H "Authorization: Bearer ${KEY}" \\\n  -F "file=@contrato.pdf" \\\n  -F 'metadata={\n    "nome": "Contrato de Serviços",\n    "countryCode": "BR",\n    "mensagem": "Por favor, assine este contrato.",\n    "signers": [\n      {\n        "nome": "João Silva",\n        "email": "joao@empresa.com",\n        "countryCode": "BR",\n        "signatureRole": "SIGNER"\n      }\n    ]\n  }'`,
      node: `import FormData from 'form-data'\nimport fs from 'fs'\n\nconst form = new FormData()\nform.append('file', fs.createReadStream('contrato.pdf'))\nform.append('metadata', JSON.stringify({\n  nome: 'Contrato de Serviços',\n  countryCode: 'BR',\n  mensagem: 'Por favor, assine este contrato.',\n  signers: [{\n    nome: 'João Silva',\n    email: 'joao@empresa.com',\n    countryCode: 'BR',\n    signatureRole: 'SIGNER'\n  }]\n}))\n\nconst res = await fetch('${BASE}/documents', {\n  method: 'POST',\n  headers: { Authorization: \`Bearer \${API_KEY}\`, ...form.getHeaders() },\n  body: form\n})\nconst { document, signers } = await res.json()`,
      python: `import requests\n\nwith open('contrato.pdf', 'rb') as f:\n    r = requests.post('${BASE}/documents',\n        headers={'Authorization': f'Bearer {API_KEY}'},\n        files={'file': f},\n        data={'metadata': json.dumps({\n            'nome': 'Contrato de Serviços',\n            'countryCode': 'BR',\n            'signers': [{\n                'nome': 'João Silva',\n                'email': 'joao@empresa.com',\n                'countryCode': 'BR',\n                'signatureRole': 'SIGNER'\n            }]\n        })}\n    )\nprint(r.json())`,
    },
    send: {
      curl: `curl -X POST ${BASE}/documents/<id>/send \\\n  -H "Authorization: Bearer ${KEY}"`,
      node: `await fetch(\`${BASE}/documents/\${docId}/send\`, {\n  method: 'POST',\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})`,
      python: `requests.post(f'${BASE}/documents/{doc_id}/send',\n    headers={'Authorization': f'Bearer {API_KEY}'})`,
    },
    status: {
      curl: `curl ${BASE}/documents/<id> \\\n  -H "Authorization: Bearer ${KEY}"`,
      node: `const res = await fetch(\`${BASE}/documents/\${docId}\`, {\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})\nconst { document } = await res.json()\n// document.status: 'DRAFT'|'PENDING'|'COMPLETED'|'EXPIRED'|'CANCELLED'\n// document.signers[].status: 'PENDING_VIEW'|...|'SIGNED'`,
      python: `r = requests.get(f'${BASE}/documents/{doc_id}',\n    headers={'Authorization': f'Bearer {API_KEY}'})\ndoc = r.json()['document']\nprint(doc['status'], [s['status'] for s in doc['signers']])`,
    },
    download: {
      curl: `# PDF original\ncurl "${BASE}/documents/<id>/download?type=original" \\\n  -H "Authorization: Bearer ${KEY}" -o original.pdf\n\n# PDF assinado (só quando status=COMPLETED)\ncurl "${BASE}/documents/<id>/download?type=final" \\\n  -H "Authorization: Bearer ${KEY}" -o assinado.pdf\n\n# ZIP com os dois\ncurl "${BASE}/documents/<id>/download-zip" \\\n  -H "Authorization: Bearer ${KEY}" -o pacote.zip`,
      node: `const res = await fetch(\`${BASE}/documents/\${docId}/download?type=final\`, {\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})\nconst buffer = await res.arrayBuffer()\nfs.writeFileSync('assinado.pdf', Buffer.from(buffer))`,
      python: `r = requests.get(f'${BASE}/documents/{doc_id}/download',\n    params={'type': 'final'},\n    headers={'Authorization': f'Bearer {API_KEY}'})\nwith open('assinado.pdf', 'wb') as f:\n    f.write(r.content)`,
    },
    list: {
      curl: `# Listar todos\ncurl "${BASE}/documents" \\\n  -H "Authorization: Bearer ${KEY}"\n\n# Apenas concluídos, pág 2\ncurl "${BASE}/documents?status=COMPLETED&page=2&limit=20" \\\n  -H "Authorization: Bearer ${KEY}"`,
      node: `const res = await fetch(\`${BASE}/documents?status=PENDING&page=1&limit=20\`, {\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})\nconst { documents, pagination } = await res.json()`,
      python: `r = requests.get(f'${BASE}/documents',\n    params={'status': 'PENDING', 'page': 1, 'limit': 20},\n    headers={'Authorization': f'Bearer {API_KEY}'})\ndata = r.json()`,
    },
    cancel: {
      curl: `curl -X DELETE ${BASE}/documents/<id> \\\n  -H "Authorization: Bearer ${KEY}"`,
      node: `await fetch(\`${BASE}/documents/\${docId}\`, {\n  method: 'DELETE',\n  headers: { Authorization: \`Bearer \${API_KEY}\` }\n})`,
      python: `requests.delete(f'${BASE}/documents/{doc_id}',\n    headers={'Authorization': f'Bearer {API_KEY}'})`,
    },
    verify: {
      curl: `# Sem autenticação — qualquer um pode verificar\ncurl https://paktdoc.com/api/verify/<codigo-verificacao>`,
      node: `const res = await fetch(\`https://paktdoc.com/api/verify/\${codigo}\`)\nconst { valid, document } = await res.json()\nif (valid) {\n  console.log('Assinado por:', document.signers.map(s => s.nome))\n}`,
      python: `r = requests.get(f'https://paktdoc.com/api/verify/{codigo}')\ndata = r.json()\nif data['valid']:\n    print('Válido:', [s['nome'] for s in data['document']['signers']])`,
    },
    webhook: {
      curl: `# Node.js — verificação de assinatura HMAC\nimport crypto from 'crypto'\n\nfunction verificarPakt(payload, header, secret) {\n  const esperado = 'sha256=' + crypto\n    .createHmac('sha256', secret)\n    .update(payload)\n    .digest('hex')\n  return crypto.timingSafeEqual(\n    Buffer.from(header),\n    Buffer.from(esperado)\n  )\n}`,
      node: `// Express\napp.post('/webhooks/pakt', express.raw({ type: 'application/json' }), (req, res) => {\n  const sig = req.headers['x-pakt-signature']\n  if (!verificarPakt(req.body, sig, process.env.PAKT_WEBHOOK_SECRET)) {\n    return res.status(401).send('Assinatura inválida')\n  }\n  const { event, documentId, data } = JSON.parse(req.body)\n  switch (event) {\n    case 'document.completed':\n      // Todos assinaram — liberar acesso, baixar PDF final\n      await baixarPDFFinal(documentId)\n      break\n    case 'signer.signed':\n      // Notificar remetente sobre progresso\n      console.log(data.signerName, 'assinou em', data.signedAt)\n      break\n    case 'signer.rejected':\n      // Identidade não confirmada — notificar e cancelar se necessário\n      break\n  }\n  res.json({ ok: true })\n})`,
      python: `import hmac, hashlib\n\ndef verificar_pakt(payload, header, secret):\n    esperado = 'sha256=' + hmac.new(\n        secret.encode(), payload, hashlib.sha256\n    ).hexdigest()\n    return hmac.compare_digest(header, esperado)\n\n@app.post('/webhooks/pakt')\ndef webhook(request):\n    sig = request.headers.get('X-Pakt-Signature')\n    if not verificar_pakt(request.body, sig, PAKT_SECRET):\n        return Response(status=401)\n    event = request.json()\n    if event['event'] == 'document.completed':\n        baixar_pdf(event['documentId'])\n    return {'ok': True}`,
    },
    multi: {
      curl: `# Documento com múltiplos signatários e campos posicionados\ncurl -X POST ${BASE}/documents \\\n  -H "Authorization: Bearer ${KEY}" \\\n  -F "file=@contrato.pdf" \\\n  -F 'metadata={\n    "nome": "Contrato Bilateral",\n    "countryCode": "BR",\n    "expiresAt": "2026-12-31T23:59:59Z",\n    "signers": [\n      {\n        "nome": "Maria Costa",\n        "email": "maria@empresa.com",\n        "countryCode": "BR",\n        "signatureRole": "SIGNER",\n        "orderIndex": 0\n      },\n      {\n        "nome": "Carlos Ramos",\n        "email": "carlos@cliente.com",\n        "countryCode": "BR",\n        "nationalId": "123.456.789-00",\n        "nationalIdType": "CPF",\n        "signatureRole": "SIGNER",\n        "orderIndex": 1\n      },\n      {\n        "nome": "Ana Lima",\n        "email": "ana@testemunha.com",\n        "countryCode": "BR",\n        "signatureRole": "WITNESS",\n        "orderIndex": 2\n      }\n    ],\n    "fields": [\n      {\n        "signerIndex": 0,\n        "tipo": "SIGNATURE",\n        "page": 3,\n        "x": 0.05, "y": 0.85,\n        "width": 0.35, "height": 0.07,\n        "required": true\n      },\n      {\n        "signerIndex": 1,\n        "tipo": "SIGNATURE",\n        "page": 3,\n        "x": 0.55, "y": 0.85,\n        "width": 0.35, "height": 0.07,\n        "required": true\n      },\n      {\n        "signerIndex": 0,\n        "tipo": "NATIONAL_ID",\n        "page": 3,\n        "x": 0.05, "y": 0.93,\n        "width": 0.35, "height": 0.04,\n        "required": true\n      }\n    ]\n  }'`,
      node: `// Documento internacional — signatários em países diferentes\nconst metadata = {\n  nome: 'International Agreement',\n  countryCode: 'US',\n  signers: [\n    {\n      nome: 'John Smith',\n      email: 'john@us-company.com',\n      countryCode: 'US',          // paga via Stripe Pay by Bank\n      signatureRole: 'SIGNER'\n    },\n    {\n      nome: 'Hans Müller',\n      email: 'hans@de-firm.de',\n      countryCode: 'DE',          // paga via SEPA\n      signatureRole: 'SIGNER'\n    },\n    {\n      nome: 'Pedro Silva',\n      email: 'pedro@br.com',\n      countryCode: 'BR',          // paga via PIX\n      signatureRole: 'SIGNER'\n    }\n  ]\n}\n// Cada signatário vê a interface em seu idioma\n// e paga com o método bancário local`,
      python: `# Acompanhar status de cada signatário\nimport time, requests\n\ndef aguardar_conclusao(doc_id, api_key, timeout=3600):\n    inicio = time.time()\n    while time.time() - inicio < timeout:\n        r = requests.get(f'${BASE}/documents/{doc_id}',\n            headers={'Authorization': f'Bearer {api_key}'})\n        doc = r.json()['document']\n        print('Status:', doc['status'])\n        for s in doc['signers']:\n            print(f"  {s['nome']}: {s['status']}")\n        if doc['status'] == 'COMPLETED':\n            return doc\n        time.sleep(30)\n    raise TimeoutError('Documento expirou')\n\n# Recomendado: usar webhooks em vez de polling`,
    },
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-gray-400 hover:text-gray-600 text-sm">← Dashboard</Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-gray-900 font-semibold">Documentação da API</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
          <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
          {BASE}
        </div>
      </header>

      {/* Nav lateral + conteúdo */}
      <div className="max-w-6xl mx-auto px-6 py-8 flex gap-8">

        {/* Sidebar */}
        <aside className="hidden lg:block w-44 flex-shrink-0">
          <nav className="sticky top-24 space-y-1 text-sm">
            {[
              ['#chave', 'Chave de API'],
              ['#autenticacao', 'Autenticação'],
              ['#endpoints', 'Endpoints'],
              ['#criar', 'Criar documento'],
              ['#enviar', 'Enviar'],
              ['#status', 'Status'],
              ['#baixar', 'Download'],
              ['#listar', 'Listar'],
              ['#cancelar', 'Cancelar'],
              ['#multi', 'Casos de uso'],
              ['#verificacao', 'Verificação'],
              ['#lifecycle', 'Lifecycle'],
              ['#schema', 'Schema'],
              ['#webhooks', 'Webhooks'],
              ['#erros', 'Erros'],
            ].map(([href, label]) => (
              <a key={href} href={href} className="block text-gray-500 hover:text-gray-900 transition-colors py-0.5">{label}</a>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <div className="flex-1 space-y-6 min-w-0">

          {/* Seletor de linguagem global */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Exemplos em:</span>
            {(['curl', 'node', 'python'] as Lang[]).map(l => (
              <button key={l} onClick={() => setLang(l)}
                className={`text-xs px-3 py-1.5 rounded font-mono border transition-colors ${lang === l ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                {l === 'node' ? 'Node.js' : l}
              </button>
            ))}
          </div>

          {/* ── Chave de API ──────────────────────────────────────── */}
          <section id="chave" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Chave de API</h2>
              <p className="text-sm text-gray-500 mt-1">Use esta chave para autenticar chamadas à API do PAKT a partir do seu servidor.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm font-mono text-gray-800 break-all select-all">
                  {keyVisible ? (apiKey ?? '...') : maskedKey}
                </code>
                <button onClick={() => setKeyVisible(v => !v)} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50" title={keyVisible ? 'Ocultar' : 'Mostrar'}>
                  {keyVisible ? '🙈' : '👁'}
                </button>
                <button onClick={copy} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50" title="Copiar">
                  {copied ? '✓' : '📋'}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">Nunca exponha no frontend ou em repositórios públicos. Só use no servidor.</p>
                <button onClick={regenerate} disabled={regenerating}
                  className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50 transition-colors">
                  {regenerating ? 'Gerando...' : '↻ Regenerar'}
                </button>
              </div>
            </div>
          </section>

          {/* ── Autenticação ──────────────────────────────────────── */}
          <section id="autenticacao" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Autenticação</h2>
              <p className="text-sm text-gray-500 mt-1">Todas as chamadas à API pública exigem o header <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">Authorization</code>.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm">
                Authorization: Bearer <span className="text-pakt-orange">{'<api-key>'}</span>
              </div>
              <p className="text-sm text-gray-600">A chave começa com <code className="bg-gray-100 px-1 rounded text-xs font-mono">pk_</code> e tem 64 caracteres hex. Pode ser regenerada a qualquer momento — a chave antiga invalida imediatamente.</p>
              <div>
                <LangTabs lang={lang} setLang={setLang} />
                <Code>{snippets.auth[lang]}</Code>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <strong>Segurança:</strong> Armazene a chave em variáveis de ambiente (<code className="font-mono text-xs">PAKT_API_KEY</code>). Nunca no código-fonte nem no frontend.
              </div>
            </div>
          </section>

          {/* ── Tabela de endpoints ───────────────────────────────── */}
          <section id="endpoints" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Endpoints</h2>
              <p className="text-sm text-gray-500 mt-1">Base URL: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{BASE}</code></p>
            </div>
            <div className="px-6 py-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Método</th>
                    <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Endpoint</th>
                    <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Descrição</th>
                    <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Auth</th>
                  </tr>
                </thead>
                <tbody>
                  <Row method="GET"    path="/me"                   desc="Dados da conta e limites"              auth="API Key" />
                  <Row method="POST"   path="/documents"            desc="Criar documento (multipart + PDF)"     auth="API Key" />
                  <Row method="GET"    path="/documents"            desc="Listar documentos (paginado)"          auth="API Key" />
                  <Row method="GET"    path="/documents/:id"        desc="Obter documento com signatários"       auth="API Key" />
                  <Row method="POST"   path="/documents/:id/send"   desc="Enviar e-mail aos signatários"         auth="API Key" />
                  <Row method="GET"    path="/documents/:id/download" desc="Baixar PDF (original ou final)"     auth="API Key" />
                  <Row method="GET"    path="/documents/:id/download-zip" desc="Baixar ZIP (original + assinado)" auth="API Key" />
                  <Row method="DELETE" path="/documents/:id"        desc="Cancelar documento"                   auth="API Key" />
                  <Row method="GET"    path="/webhooks"             desc="Listar endpoints de webhook"           auth="API Key" />
                  <Row method="POST"   path="/webhooks"             desc="Criar endpoint de webhook"             auth="API Key" />
                  <Row method="DELETE" path="/webhooks/:id"         desc="Remover endpoint de webhook"           auth="API Key" />
                  <tr className="border-b border-gray-100">
                    <td className="py-2.5 pr-3"><Pill color="bg-blue-100 text-blue-700">GET</Pill></td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-gray-800">/api/verify/:codigo</td>
                    <td className="py-2.5 pr-4 text-sm text-gray-600">Verificar autenticidade de documento</td>
                    <td className="py-2.5 text-xs text-gray-400">Público</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Criar documento ──────────────────────────────────── */}
          <section id="criar" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Criar documento</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-green-100 text-green-700">POST</Pill> <code className="ml-1 text-xs font-mono">/v1/documents</code> — multipart/form-data com PDF + metadados JSON.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.create[lang]}</Code>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Resposta (201):</p>
                <Code>{`{
  "document": {
    "id": "uuid",
    "nome": "Contrato de Serviços",
    "status": "DRAFT",
    "hashOriginal": "sha256-hex",
    "codigoVerificacao": "ABC123XY",
    "countryCode": "BR",
    "createdAt": "2026-04-23T14:00:00.000Z"
  },
  "signers": [
    {
      "id": "uuid",
      "nome": "João Silva",
      "email": "joao@empresa.com",
      "countryCode": "BR",
      "signUrl": "https://paktdoc.com/sign/<token>",
      "tokenAcesso": "<token>",
      "status": "PENDING_VIEW"
    }
  ]
}`}</Code>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-800">
                <strong>Importante:</strong> O documento é criado em status <code className="font-mono text-xs">DRAFT</code>. Os signatários só recebem o link após chamar <code className="font-mono text-xs">POST /send</code>. Isso permite posicionar os campos de assinatura antes de enviar.
              </div>
            </div>
          </section>

          {/* ── Enviar ───────────────────────────────────────────── */}
          <section id="enviar" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Enviar para assinatura</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-green-100 text-green-700">POST</Pill> <code className="ml-1 text-xs font-mono">/v1/documents/:id/send</code> — dispara e-mails e muda status para <code className="font-mono text-xs">PENDING</code>.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.send[lang]}</Code>
              <Code>{`// Resposta (200)
{ "message": "Emails enviados", "signerCount": 1 }`}</Code>
            </div>
          </section>

          {/* ── Status ───────────────────────────────────────────── */}
          <section id="status" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Consultar status</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-blue-100 text-blue-700">GET</Pill> <code className="ml-1 text-xs font-mono">/v1/documents/:id</code> — retorna documento completo com signatários e trilha de auditoria.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.status[lang]}</Code>
              <Code>{`// Resposta
{
  "document": {
    "id": "uuid",
    "nome": "Contrato de Serviços",
    "status": "PENDING",
    "hashOriginal": "sha256-hex",
    "hashFinal": null,              // preenchido quando COMPLETED
    "codigoVerificacao": "ABC123XY",
    "createdAt": "2026-04-23T14:00:00.000Z",
    "completedAt": null,
    "signers": [
      {
        "id": "uuid",
        "nome": "João Silva",
        "email": "joao@empresa.com",
        "countryCode": "BR",
        "signUrl": "https://paktdoc.com/sign/<token>",
        "status": "PENDING_PAYMENT",
        "paymentMethod": "pix",
        "paymentStatus": "PENDING",
        "signedAt": null
      }
    ],
    "auditLogs": [
      { "evento": "DOCUMENT_CREATED",  "createdAt": "...", "signerName": null },
      { "evento": "DOCUMENT_SENT",     "createdAt": "...", "signerName": null },
      { "evento": "DOCUMENT_VIEWED",   "createdAt": "...", "signerName": "João Silva" },
      { "evento": "SELFIE_CAPTURED",   "createdAt": "...", "signerName": "João Silva" },
      { "evento": "PAYMENT_GENERATED", "createdAt": "...", "signerName": "João Silva" }
    ]
  }
}`}</Code>
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-sm text-amber-800">
                Prefira <strong>webhooks</strong> ao polling. O polling consome rate limit (100 req/min) e atrasa a resposta. Configure <code className="font-mono text-xs">document.completed</code> e você é notificado imediatamente.
              </div>
            </div>
          </section>

          {/* ── Download ─────────────────────────────────────────── */}
          <section id="baixar" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Baixar PDF</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-blue-100 text-blue-700">GET</Pill> <code className="ml-1 text-xs font-mono">/v1/documents/:id/download?type=original|final</code></p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.download[lang]}</Code>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200"><th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500">Parâmetro</th><th className="text-left py-2 text-xs font-semibold text-gray-500">Descrição</th></tr></thead>
                  <tbody>
                    <tr className="border-b border-gray-100"><td className="py-2 pr-4 font-mono text-xs">type=original</td><td className="py-2 text-sm text-gray-600">PDF enviado pelo remetente (sem assinaturas)</td></tr>
                    <tr className="border-b border-gray-100"><td className="py-2 pr-4 font-mono text-xs">type=final</td><td className="py-2 text-sm text-gray-600">PDF com PAdES seal e trilha embutida — só disponível quando <code className="font-mono text-xs">status=COMPLETED</code></td></tr>
                    <tr className="border-b border-gray-100"><td className="py-2 pr-4 font-mono text-xs">/download-zip</td><td className="py-2 text-sm text-gray-600">ZIP com ambos os arquivos</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ── Listar ───────────────────────────────────────────── */}
          <section id="listar" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Listar documentos</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-blue-100 text-blue-700">GET</Pill> <code className="ml-1 text-xs font-mono">/v1/documents?status=&page=&limit=</code></p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.list[lang]}</Code>
              <Code>{`// Resposta
{
  "documents": [ { ...document } ],
  "pagination": { "page": 1, "limit": 20, "total": 47, "totalPages": 3 }
}`}</Code>
              <div className="text-sm text-gray-600">
                <strong>Filtros disponíveis:</strong>{' '}
                <code className="bg-gray-100 px-1 rounded text-xs font-mono">status=DRAFT|PENDING|COMPLETED|EXPIRED|CANCELLED</code>
              </div>
            </div>
          </section>

          {/* ── Cancelar ─────────────────────────────────────────── */}
          <section id="cancelar" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Cancelar documento</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-red-100 text-red-700">DELETE</Pill> <code className="ml-1 text-xs font-mono">/v1/documents/:id</code> — irreversível.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.cancel[lang]}</Code>
              <p className="text-sm text-gray-600">Documentos em status <code className="font-mono text-xs">COMPLETED</code> não podem ser cancelados. Cancela signatários pendentes e dispara webhook <code className="font-mono text-xs">document.cancelled</code>.</p>
            </div>
          </section>

          {/* ── Casos de uso avançados ───────────────────────────── */}
          <section id="multi" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Casos de uso</h2>
              <p className="text-sm text-gray-500 mt-1">Múltiplos signatários, campos posicionados e documentos internacionais.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.multi[lang]}</Code>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Tipos de campo (<code className="font-mono text-xs">fields[].tipo</code>):</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200"><th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500">Tipo</th><th className="text-left py-2 text-xs font-semibold text-gray-500">Descrição</th></tr></thead>
                    <tbody>
                      {[['SIGNATURE','Assinatura do signatário (desenhada, digitada ou imagem)'],['RUBRIC','Rubrica (assinatura resumida)'],['NAME','Nome completo por extenso'],['NATIONAL_ID','CPF ou documento equivalente do país'],['DATE','Data de assinatura (preenchida automaticamente)'],['TEXT','Campo de texto livre'],['CHECKBOX','Checkbox de aceite']].map(([t, d]) => (
                        <tr key={t} className="border-b border-gray-100"><td className="py-2 pr-4 font-mono text-xs">{t}</td><td className="py-2 text-sm text-gray-600">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Posicionamento de campos (coordenadas relativas ao PDF):</p>
                <Code>{`// Coordenadas normalizadas: 0.0 = esquerda/topo, 1.0 = direita/base
// x=0.05  → 5% da largura da página a partir da esquerda
// y=0.85  → 85% da altura a partir do topo
// Dica: use /documents/:id/fields (PUT) para ajustar após criar o rascunho
{
  "signerIndex": 0,   // índice na lista signers[] (0-based)
  "tipo": "SIGNATURE",
  "page": 1,          // número da página (começa em 1)
  "x": 0.05,          // posição horizontal (0.0 – 1.0)
  "y": 0.85,          // posição vertical (0.0 – 1.0)
  "width": 0.35,      // largura relativa
  "height": 0.07,     // altura relativa
  "required": true
}`}</Code>
              </div>
            </div>
          </section>

          {/* ── Verificação pública ──────────────────────────────── */}
          <section id="verificacao" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Verificação pública</h2>
              <p className="text-sm text-gray-500 mt-1"><Pill color="bg-blue-100 text-blue-700">GET</Pill> <code className="ml-1 text-xs font-mono">https://paktdoc.com/api/verify/:codigo</code> — sem autenticação. Qualquer sistema pode verificar.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.verify[lang]}</Code>
              <Code>{`// Resposta (200 — válido)
{
  "valid": true,
  "document": {
    "id": "uuid",
    "nome": "Contrato de Serviços",
    "status": "COMPLETED",
    "hashOriginal": "sha256-hex",
    "hashFinal":    "sha256-hex",
    "codigoVerificacao": "ABC123XY",
    "createdAt": "...",
    "completedAt": "...",
    "createdBy": "Nome do Remetente",
    "countryCode": "BR",
    "signers": [
      {
        "nome": "João Silva",
        "email": "jo***@empresa.com",   // mascarado
        "countryCode": "BR",
        "status": "SIGNED",
        "paymentMethod": "pix",
        "paymentIdentityStrength": "strong",
        "paymentPaidAt": "...",
        "signedAt": "...",
        "signatureMethod": "drawn",
        "signCity": "São Paulo"
      }
    ]
  },
  "auditLog": [
    { "evento": "DOCUMENT_CREATED",     "createdAt": "...", "signerName": null },
    { "evento": "PAYMENT_CONFIRMED",    "createdAt": "...", "signerName": "João Silva" },
    { "evento": "SIGNATURE_COMPLETED",  "createdAt": "...", "signerName": "João Silva" }
  ]
}

// Resposta (404 — inválido)
{ "valid": false, "error": "Documento não encontrado" }`}</Code>
              <p className="text-sm text-gray-600">O <code className="font-mono text-xs">codigoVerificacao</code> está embutido no PDF assinado (capa + QR code). Sistemas terceiros podem chamar este endpoint para confirmar autenticidade sem precisar de conta no PAKT.</p>
            </div>
          </section>

          {/* ── Lifecycle ────────────────────────────────────────── */}
          <section id="lifecycle" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Lifecycle do documento</h2>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Status do documento:</p>
                <div className="flex flex-wrap gap-2 items-center text-sm">
                  {[['DRAFT','gray','Criado, não enviado'],['PENDING','yellow','Aguardando assinaturas'],['COMPLETED','green','Todos assinaram'],['EXPIRED','red','Prazo vencido'],['CANCELLED','red','Cancelado']].map(([s, c, d]) => (
                    <div key={s} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <span className={`w-2 h-2 rounded-full bg-${c}-400 flex-shrink-0`} />
                      <code className="text-xs font-mono">{s}</code>
                      <span className="text-xs text-gray-500">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Status do signatário (fluxo linear):</p>
                <div className="flex flex-wrap gap-1 items-center text-xs font-mono">
                  {['PENDING_VIEW','PENDING_SELFIE','PENDING_PAYMENT','PENDING_SIGNATURE','SIGNED'].map((s, i, arr) => (
                    <span key={s} className="flex items-center gap-1">
                      <span className="bg-gray-100 border border-gray-200 rounded px-2 py-1">{s}</span>
                      {i < arr.length - 1 && <span className="text-gray-400">→</span>}
                    </span>
                  ))}
                  <span className="text-gray-400 ml-1">|</span>
                  <span className="bg-red-100 border border-red-200 text-red-700 rounded px-2 py-1 ml-1">REJECTED</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">REJECTED ocorre quando a identidade do pagador PIX não confere com o signatário cadastrado.</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Eventos de auditoria (<code className="font-mono text-xs">auditLogs[].evento</code>):</p>
                <div className="grid grid-cols-2 gap-1">
                  {['DOCUMENT_CREATED','DOCUMENT_SENT','DOCUMENT_VIEWED','SELFIE_CAPTURED','PAYMENT_GENERATED','PAYMENT_CONFIRMED','IDENTITY_MISMATCH','SIGNATURE_COMPLETED','DOCUMENT_FINALIZED','VERIFICATION_ACCESSED'].map(e => (
                    <code key={e} className="text-xs bg-gray-50 border border-gray-100 rounded px-2 py-1 font-mono">{e}</code>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── Schema completo ──────────────────────────────────── */}
          <section id="schema" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Schema — metadata do documento</h2>
              <p className="text-sm text-gray-500 mt-1">Campo <code className="font-mono text-xs">metadata</code> enviado como string JSON no multipart.</p>
            </div>
            <div className="px-6 py-5">
              <Code>{`{
  // ── Obrigatório ──────────────────────────────────────
  "nome": "string",                         // Nome do documento
  "signers": [Signer],                      // Ao menos 1 signatário

  // ── Opcional ─────────────────────────────────────────
  "countryCode": "BR",                      // País do documento (default: BR)
  "mensagem": "string",                     // Mensagem no e-mail de convite
  "expiresAt": "2026-12-31T23:59:59Z",      // ISO 8601 — deadline de assinatura
  "fields": [Field],                        // Campos posicionados no PDF

  // ── Signer ───────────────────────────────────────────
  // {
  //   "nome": "string",                    // obrigatório
  //   "email": "string",                   // obrigatório
  //   "countryCode": "BR",                 // país do signatário (define método de pagamento)
  //   "signatureRole": "SIGNER",           // SIGNER | WITNESS | APPROVER
  //   "orderIndex": 0,                     // ordem de assinatura (default: 0)
  //   "telefone": "+5511999999999",        // opcional
  //   "nationalId": "123.456.789-00",      // CPF ou equiv. — para validação PIX
  //   "nationalIdType": "CPF"              // CPF | CNPJ | PASSPORT | NIF | ...
  // }

  // ── Field ────────────────────────────────────────────
  // {
  //   "signerIndex": 0,                    // índice em signers[] (0-based)
  //   "tipo": "SIGNATURE",                 // ver tipos acima
  //   "page": 1,                           // número da página (1-based)
  //   "x": 0.05,                           // posição horizontal 0.0 – 1.0
  //   "y": 0.85,                           // posição vertical 0.0 – 1.0
  //   "width": 0.35,                       // largura 0.0 – 1.0
  //   "height": 0.07,                      // altura 0.0 – 1.0
  //   "required": true
  // }
}`}</Code>
            </div>
          </section>

          {/* ── Webhooks ─────────────────────────────────────────── */}
          <section id="webhooks" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Webhooks</h2>
                <p className="text-sm text-gray-500 mt-1">Notificações em tempo real para o seu servidor quando documentos forem assinados.</p>
              </div>
              <button onClick={() => setShowForm(v => !v)} className="text-sm bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                + Adicionar
              </button>
            </div>

            {/* Formulário */}
            {showForm && (
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL do endpoint *</label>
                  <input type="url" value={newUrl} onChange={e => setNewUrl(e.target.value)}
                    placeholder="https://sua-plataforma.com/webhooks/pakt"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-400 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Eventos *</label>
                  <div className="space-y-2">
                    {ALL_EVENTS.map(ev => (
                      <label key={ev.value} className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={newEvents.includes(ev.value)}
                          onChange={() => setNewEvents(prev => prev.includes(ev.value) ? prev.filter(e => e !== ev.value) : [...prev, ev.value])}
                          className="mt-0.5" />
                        <div>
                          <span className="text-sm font-mono text-gray-800">{ev.value}</span>
                          <span className="text-xs text-gray-500 ml-2">— {ev.desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowForm(false)} className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancelar</button>
                  <button onClick={addWebhook} disabled={addingWebhook || !newUrl || newEvents.length === 0}
                    className="text-sm bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50">
                    {addingWebhook ? 'Criando...' : 'Criar endpoint'}
                  </button>
                </div>
              </div>
            )}

            {/* Lista */}
            <div className="divide-y divide-gray-100">
              {loadingWebhooks && <div className="px-6 py-4 text-sm text-gray-400">Carregando...</div>}
              {!loadingWebhooks && webhooks.length === 0 && (
                <div className="px-6 py-8 text-center text-sm text-gray-400">Nenhum webhook configurado.</div>
              )}
              {webhooks.map(w => (
                <div key={w.id} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${w.active ? 'bg-green-400' : 'bg-gray-300'}`} />
                        <code className="text-sm font-mono text-gray-800 truncate">{w.url}</code>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {w.events.map(ev => <span key={ev} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{ev}</span>)}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Segredo:</span>
                        <code className="font-mono bg-gray-50 px-2 py-0.5 rounded border">
                          {revealedSecrets[w.id] ? w.secret : `${w.secret.slice(0, 10)}••••••••`}
                        </code>
                        <button onClick={() => setRevealedSecrets(p => ({ ...p, [w.id]: !p[w.id] }))} className="text-gray-400 hover:text-gray-600">
                          {revealedSecrets[w.id] ? '🙈' : '👁'}
                        </button>
                      </div>
                    </div>
                    <button onClick={() => deleteWebhook(w.id)} className="text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-lg px-2 py-1 hover:bg-red-50 flex-shrink-0">
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Docs de payload */}
            <div className="px-6 py-5 border-t border-gray-100 space-y-4">
              <p className="text-sm font-medium text-gray-700">Estrutura do payload recebido:</p>
              <Code>{`// Headers
X-Pakt-Signature: sha256=<hmac-sha256>
X-Pakt-Event: document.completed
Content-Type: application/json
User-Agent: PAKT-Webhook/1.0

// Body — document.completed
{
  "event": "document.completed",
  "documentId": "uuid",
  "timestamp": "2026-04-23T15:30:00.000Z",
  "data": {
    "documentId": "uuid",
    "nome": "Contrato de Serviços",
    "status": "COMPLETED",
    "completedAt": "2026-04-23T15:30:00.000Z",
    "signers": [{ "nome": "João Silva", "signedAt": "..." }]
  }
}

// Body — signer.signed
{
  "event": "signer.signed",
  "documentId": "uuid",
  "timestamp": "2026-04-23T15:30:00.000Z",
  "data": {
    "documentId": "uuid",
    "signerId": "uuid",
    "signerName": "João Silva",
    "signerEmail": "joao@empresa.com",
    "signedAt": "2026-04-23T15:30:00.000Z"
  }
}

// Body — signer.rejected
{
  "event": "signer.rejected",
  "documentId": "uuid",
  "data": {
    "documentId": "uuid",
    "signerId": "uuid",
    "reason": "identity_mismatch"
  }
}`}</Code>
              <p className="text-sm font-medium text-gray-700">Verificar assinatura HMAC:</p>
              <LangTabs lang={lang} setLang={setLang} />
              <Code>{snippets.webhook[lang]}</Code>
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-800">
                <strong>Retry:</strong> O PAKT faz 1 tentativa de entrega com timeout de 10s. Responda com HTTP 2xx em menos de 10 segundos — processe o evento de forma assíncrona se necessário.
              </div>
            </div>
          </section>

          {/* ── Erros ────────────────────────────────────────────── */}
          <section id="erros" className="bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-6">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Erros</h2>
              <p className="text-sm text-gray-500 mt-1">Todos os erros retornam JSON com <code className="font-mono text-xs">error</code> e opcionalmente <code className="font-mono text-xs">message</code>.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <Code>{`// Estrutura padrão de erro
{ "error": "Unauthorized", "message": "API key inválida ou expirada" }

// Erro de validação
{ "error": "Bad Request", "message": "O campo 'signers' é obrigatório" }`}</Code>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b-2 border-gray-200"><th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500">HTTP</th><th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500">Quando ocorre</th><th className="text-left py-2 text-xs font-semibold text-gray-500">Como resolver</th></tr></thead>
                  <tbody>
                    {[
                      ['400','Payload inválido, campo obrigatório ausente, arquivo não é PDF','Verificar o schema do metadata e o arquivo enviado'],
                      ['401','API Key ausente, inválida ou regenerada','Verificar o header Authorization: Bearer'],
                      ['403','Operação não permitida (ex: cancelar documento já concluído)','Verificar o status atual do documento'],
                      ['404','Documento, signatário ou webhook não encontrado','Verificar o ID ou código de verificação'],
                      ['409','Conflito — ex: e-mail já cadastrado no registro','Verificar unicidade do recurso'],
                      ['413','Arquivo PDF maior que 20MB','Comprimir o PDF antes de enviar'],
                      ['429','Rate limit excedido (100 req/min)','Adicionar backoff exponencial; usar webhooks no lugar de polling'],
                      ['500','Erro interno do servidor','Tentar novamente; se persistir, contato: api@paktdoc.com'],
                    ].map(([code, cause, fix]) => (
                      <tr key={code} className="border-b border-gray-100">
                        <td className="py-2.5 pr-4"><Pill color="bg-red-100 text-red-700">{code}</Pill></td>
                        <td className="py-2.5 pr-4 text-sm text-gray-600">{cause}</td>
                        <td className="py-2.5 text-sm text-gray-500">{fix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ── Rate limit ───────────────────────────────────────── */}
          <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Rate limits & suporte</h2>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-600">
              <div className="flex gap-8">
                <div><span className="font-medium text-gray-800">Geral:</span> 100 req/min por IP</div>
                <div><span className="font-medium text-gray-800">Endpoint /sign:</span> 10 req/min por IP</div>
              </div>
              <p>Headers de resposta incluem <code className="font-mono text-xs">X-RateLimit-Remaining</code> e <code className="font-mono text-xs">X-RateLimit-Reset</code>.</p>
              <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-6">
                <span>Suporte técnico: <a href="mailto:api@paktdoc.com" className="text-pakt-orange hover:underline font-medium">api@paktdoc.com</a></span>
                <span>Status da API: <a href="https://status.paktdoc.com" className="text-pakt-orange hover:underline font-medium">status.paktdoc.com</a></span>
                <span>Ambiente de testes: adicione <code className="font-mono text-xs">?sandbox=true</code> (em breve)</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}
